// src/hooks/useOptimizeTaskContent.ts
// 管理 Task 內容優化的 AI 請求狀態：loading、直接套用、undo

import { useState, useCallback } from "react";
import { Task } from "../types/task";
import { taskService } from "../features/task";
import { logError } from "../utils/logger";

interface OptimizeState {
  isLoading: boolean;
  taskId: string | null;
}

const INITIAL_STATE: OptimizeState = {
  isLoading: false,
  taskId: null,
};

interface UseOptimizeTaskContentReturn {
  optimizeState: OptimizeState;
  requestOptimize: (
    task: Task,
    userId: string,
  ) => Promise<(() => Promise<void>) | null>;
}

/**
 * 管理 Task 內容優化的 AI 請求與直接套用流程
 *
 * 使用流程：
 * 1. requestOptimize(task, userId) → 發送 AI 請求，isLoading 期間顯示 spinner
 * 2. AI 回應後直接呼叫 updateTask 套用結果
 * 3. 回傳 undo 函數，供呼叫方透過 toast 提供 Undo 操作
 * 4. 若 AI 失敗，回傳 null（呼叫方自行 show error toast）
 *
 * 套用策略（用戶設定優先）：
 * - category：task 原本有值就保留，原本為空才採用 AI 建議
 * - objectiveId：task 原本有值就保留，原本為空才採用 AI 建議
 */
export function useOptimizeTaskContent(): UseOptimizeTaskContentReturn {
  const [optimizeState, setOptimizeState] =
    useState<OptimizeState>(INITIAL_STATE);

  const requestOptimize = useCallback(
    async (
      task: Task,
      userId: string,
    ): Promise<(() => Promise<void>) | null> => {
      setOptimizeState({ isLoading: true, taskId: task.id });

      try {
        const result = await taskService.optimizeTaskContent(
          {
            title: task.title,
            notes: task.notes,
            category: task.category,
            objectiveId: task.objectiveId,
          },
          userId,
        );

        const previousTitle = task.title;
        const previousNotes = task.notes;
        const previousCategory = task.category;
        const previousObjectiveId = task.objectiveId;

        // 保留用戶已設定的 category/objectiveId，只在空白時套用 AI 建議
        const newCategory = task.category ?? result.suggestedCategory;
        const newObjectiveId = task.objectiveId ?? result.suggestedGoalId ?? undefined;

        await taskService.updateTask(
          task.id,
          {
            title: result.optimizedTitle,
            notes: result.optimizedNotes,
            category: newCategory,
            objectiveId: newObjectiveId,
          },
          userId,
        );

        // 回傳 undo 函數：還原四個欄位至呼叫前的完整狀態
        const undo = async () => {
          await taskService.updateTask(
            task.id,
            {
              title: previousTitle,
              notes: previousNotes,
              category: previousCategory,
              objectiveId: previousObjectiveId,
            },
            userId,
          );
        };

        return undo;
      } catch (error) {
        logError("useOptimizeTaskContent: AI 優化失敗", error);
        return null;
      } finally {
        setOptimizeState(INITIAL_STATE);
      }
    },
    [],
  );

  return { optimizeState, requestOptimize };
}
